import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { useSaveIndicator } from '../../hooks/useSaveIndicator';
import { SaveToast } from '../../components/SaveToast';
import {
  PLATFORM_LINKEDIN,
  PLATFORM_INSTAGRAM,
  PLATFORM_FACEBOOK,
  CHAR_LIMITS,
  FOLD_POINTS,
  TONES,
} from '../../lib/constants';

const PREVIEW_PLATFORMS = [PLATFORM_LINKEDIN, PLATFORM_INSTAGRAM, PLATFORM_FACEBOOK];

// Fallback used when the Worker is unreachable. Same shape as the API.
const MOCK_DRAFT = {
  id: 'mock-draft',
  eventId: null,
  platform: PLATFORM_LINKEDIN,
  contentType: 'post',
  contentPhase: 'standalone',
  version: 3,
  status: 'draft',
  tone: 'celebratory',
  captionPl: `🏛️ Dziedzictwo to nie tylko przeszłość — to fundament naszej przyszłości.

W czerwcu 2026 r. spotykamy się w ICE Kraków na Heritage Summit. ✨

Dołącz do dyskusji 🌍`,
  captionEn: `🏛️ Heritage isn't just the past — it's the foundation of our future.

In June 2026 we're meeting at ICE Kraków for the Heritage Summit. ✨

Join the conversation 🌍`,
  hashtags: ['#HeritageSummit2026', '#F8M', '#CulturalHeritage'],
  tags: ['@UNESCO_PL', '@EuropaNostra'],
  mediaIds: [],
};

// ─────────────────────────────────────────────────────────────────────────────
// Platform mockup (read-only preview)
// ─────────────────────────────────────────────────────────────────────────────
function PlatformMockup({ platform, caption, foldPoint }) {
  const { t } = useTranslation();
  const truncated = caption.length > foldPoint;
  const visible = truncated ? caption.slice(0, foldPoint).trimEnd() : caption;
  const seeMoreText = platform === PLATFORM_INSTAGRAM ? t('draft.preview.more') : t('draft.preview.seeMore');

  const authorKey =
    platform === PLATFORM_LINKEDIN  ? 'draft.preview.linkedinAuthor' :
    platform === PLATFORM_INSTAGRAM ? 'draft.preview.instagramAuthor' :
                                      'draft.preview.facebookAuthor';

  const meta =
    platform === PLATFORM_LINKEDIN
      ? `${t('draft.preview.followers', { count: '1,234' })} · ${t('draft.preview.timeAgo')}`
      : t('draft.preview.timeAgo');

  return (
    <article className={`preview-mockup preview-mockup--${platform}`}>
      <div className="preview-mockup__platform-bar" />
      <header className="preview-mockup__header">
        {platform === PLATFORM_INSTAGRAM ? (
          <div className="preview-mockup__avatar">
            <div className="preview-mockup__avatar-inner">F8M</div>
          </div>
        ) : (
          <div className="preview-mockup__avatar">F8M</div>
        )}
        <div>
          <div className="preview-mockup__author">{t(authorKey)}</div>
          <div className="preview-mockup__author-meta">{meta}</div>
        </div>
      </header>

      <div className="preview-mockup__body">
        {visible || <span style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>—</span>}
        {truncated && <span className="preview-mockup__see-more">… {seeMoreText}</span>}
      </div>

      <div className="preview-mockup__image" aria-hidden="true">📷</div>

      <div className="preview-mockup__engagement">
        {platform === PLATFORM_LINKEDIN && (
          <>
            <button type="button">👍 {t('draft.actions.linkedin.like')}</button>
            <button type="button">💬 {t('draft.actions.linkedin.comment')}</button>
            <button type="button">🔄 {t('draft.actions.linkedin.repost')}</button>
            <button type="button">📤 {t('draft.actions.linkedin.send')}</button>
          </>
        )}
        {platform === PLATFORM_INSTAGRAM && (
          <>
            <button type="button">❤️</button>
            <button type="button">💬</button>
            <button type="button">📤</button>
            <button type="button" style={{ marginLeft: 'auto' }}>🔖</button>
          </>
        )}
        {platform === PLATFORM_FACEBOOK && (
          <>
            <button type="button">👍 {t('draft.actions.facebook.like')}</button>
            <button type="button">💬 {t('draft.actions.facebook.comment')}</button>
            <button type="button">🔄 {t('draft.actions.facebook.share')}</button>
          </>
        )}
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// "New draft" mode — empty editor with platform picker, POSTs on Create.
// ─────────────────────────────────────────────────────────────────────────────
function NewDraftView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [platform, setPlatform] = useState(PLATFORM_LINKEDIN);
  const [captionPl, setCaptionPl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleCreate() {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const created = await api.drafts.create({
        platform,
        captionPl: captionPl || ' ',
        contentPhase: 'standalone',
      });
      navigate(`/drafts/${created.id}`, { replace: true });
    } catch (err) {
      setError(err?.message || t('draft.create.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <Link to="/" className="draft-editor__back">{t('draft.backToDashboard')}</Link>
      <header className="draft-editor__header">
        <div className="draft-editor__title-group">
          <h1>{t('draft.newMode.title')}</h1>
        </div>
      </header>
      <div className="caption-editor" style={{ marginBottom: 'var(--space-4)' }}>
        <p className="page-header__subtitle" style={{ marginBottom: 'var(--space-4)' }}>
          {t('draft.newMode.subtitle')}
        </p>
        <div className="form-field" style={{ marginBottom: 'var(--space-4)' }}>
          <span className="form-field__label">{t('draft.create.platformLabel')}</span>
          <div className="radio-group">
            {[PLATFORM_LINKEDIN, PLATFORM_INSTAGRAM, PLATFORM_FACEBOOK].map((p) => (
              <label key={p}>
                <input
                  type="radio"
                  name="new-draft-platform"
                  checked={platform === p}
                  onChange={() => setPlatform(p)}
                />
                {t(`platforms.${p}`)}
              </label>
            ))}
          </div>
        </div>
        <textarea
          className="caption-editor__textarea"
          value={captionPl}
          placeholder={t('draft.create.captionPlaceholder')}
          onChange={(e) => setCaptionPl(e.target.value)}
        />
      </div>

      {error && <div className="modal__error" role="alert">{error}</div>}

      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <button
          type="button"
          className="button button--primary"
          onClick={handleCreate}
          disabled={submitting}
        >
          {submitting ? t('draft.create.submitting') : t('draft.newMode.createButton')}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Confirm-delete dialog (no name typing — drafts are cheap to recreate)
// ─────────────────────────────────────────────────────────────────────────────
function ConfirmDeleteDialog({ onCancel, onConfirm, deleting }) {
  const { t } = useTranslation();
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => !deleting && onCancel()}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal__title">{t('draft.delete.title')}</h2>
        <p className="modal__body">{t('draft.delete.body')}</p>
        <div className="modal__actions">
          <button type="button" className="button button--ghost" onClick={onCancel} disabled={deleting}>
            {t('common.cancel')}
          </button>
          <button type="button" className="button button--danger" onClick={onConfirm} disabled={deleting}>
            {t('draft.delete.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Top-level dispatcher: routes "new" to <NewDraftView>, everything else to
// <ExistingDraftView>. Splitting keeps Rules of Hooks happy (each subcomponent
// has a stable set of hooks).
// ─────────────────────────────────────────────────────────────────────────────
export function DraftEditor() {
  const { draftId } = useParams();
  if (draftId === 'new') return <NewDraftView />;
  return <ExistingDraftView draftId={draftId} />;
}

function ExistingDraftView({ draftId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status: saveStatus, setSaving, flashSaved, flashError } = useSaveIndicator();
  const [draft, setDraft] = useState(null);
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [usingMockData, setUsingMockData] = useState(false);
  const [previewPlatform, setPreviewPlatform] = useState(PLATFORM_LINKEDIN);
  const [showTranslation, setShowTranslation] = useState(true);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [copyFlash, setCopyFlash] = useState(false);

  // Caption auto-save: blur fires immediately, but if the user keeps typing
  // before blur we also schedule a 2s debounced flush.
  const debounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setNotFound(false);
    setUsingMockData(false);

    api.drafts.get(draftId)
      .then((data) => {
        if (cancelled) return;
        setDraft(data);
        setSavedSnapshot(data);
        setPreviewPlatform(data.platform);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 404) {
          setNotFound(true);
        } else {
          console.warn('[draft] API unreachable, using mock data:', err.message);
          setDraft(MOCK_DRAFT);
          setSavedSnapshot(MOCK_DRAFT);
          setPreviewPlatform(MOCK_DRAFT.platform);
          setUsingMockData(true);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [draftId]);

  // Fetch history once we have a real draft.
  useEffect(() => {
    if (!draft || !draftId || usingMockData) return undefined;
    let cancelled = false;
    api.drafts.history(draftId)
      .then((data) => {
        if (!cancelled) setHistory(data?.history ?? []);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      });
    return () => { cancelled = true; };
  }, [draftId, draft?.version, usingMockData]);

  // Cleanup the debounce timer on unmount.
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  function updateLocal(field, value) {
    setDraft((prev) => ({ ...prev, [field]: value }));
  }

  // PATCH a single field. Compares against `savedSnapshot` to skip no-op writes.
  async function patchField(field, value) {
    if (!draft || !draftId || draftId === 'new' || usingMockData) return;
    const prevValue = savedSnapshot?.[field];
    if (Array.isArray(prevValue) && Array.isArray(value)) {
      if (prevValue.length === value.length && prevValue.every((v, i) => v === value[i])) return;
    } else if (prevValue === value) {
      return;
    } else if ((prevValue == null || prevValue === '') && (value == null || value === '')) {
      return;
    }

    setSaving();
    try {
      const updated = await api.drafts.update(draftId, { [field]: value });
      setDraft(updated);
      setSavedSnapshot(updated);
      flashSaved();
    } catch (err) {
      console.error('[draft] save failed:', err);
      setDraft((prev) => ({ ...prev, [field]: prevValue }));
      flashError();
    }
  }

  function handleCaptionBlur() {
    clearTimeout(debounceRef.current);
    patchField('captionPl', draft.captionPl);
  }

  function scheduleCaptionAutosave() {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      patchField('captionPl', draft.captionPl);
    }, 2000);
  }

  function setHashtags(next) { patchField('hashtags', next); setDraft((p) => ({ ...p, hashtags: next })); }
  function setTags(next)     { patchField('tags', next);     setDraft((p) => ({ ...p, tags: next })); }

  function addHashtag(value) {
    let cleaned = value.trim().replace(/\s+/g, '');
    if (!cleaned) return;
    if (!cleaned.startsWith('#')) cleaned = '#' + cleaned;
    if (draft.hashtags.includes(cleaned)) return;
    setHashtags([...draft.hashtags, cleaned]);
  }
  function removeHashtag(value) {
    setHashtags(draft.hashtags.filter((h) => h !== value));
  }
  function addTag(value) {
    let cleaned = value.trim();
    if (!cleaned) return;
    if (!cleaned.startsWith('@')) cleaned = '@' + cleaned;
    if (draft.tags.includes(cleaned)) return;
    setTags([...draft.tags, cleaned]);
  }
  function removeTag(value) {
    setTags(draft.tags.filter((tag) => tag !== value));
  }

  async function handleStatusChange(nextStatus) {
    await patchField('status', nextStatus);
  }

  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      setCopyFlash(true);
      setTimeout(() => setCopyFlash(false), 1500);
    } catch (err) {
      console.error('[draft] clipboard write failed:', err);
    }
  }

  async function handleDelete() {
    if (!draft || usingMockData) { setShowDelete(false); return; }
    setDeleting(true);
    try {
      await api.drafts.delete(draft.id);
      navigate(draft.eventId ? `/events/${draft.eventId}` : '/');
    } catch (err) {
      console.error('[draft] delete failed:', err);
      flashError();
      setDeleting(false);
      setShowDelete(false);
    }
  }

  // ── Loading / not-found states ────────────────────────────────────────
  if (isLoading) {
    return (
      <div>
        <Link to="/" className="draft-editor__back">{t('draft.backToDashboard')}</Link>
        <div className="empty-state">
          <div className="empty-state__icon" aria-hidden="true">⏳</div>
          <p>{t('draft.loading')}</p>
        </div>
      </div>
    );
  }
  if (notFound || !draft) {
    return (
      <div>
        <Link to="/" className="draft-editor__back">{t('draft.backToDashboard')}</Link>
        <div className="empty-state">
          <div className="empty-state__icon" aria-hidden="true">📭</div>
          <h2>{t('draft.notFoundTitle')}</h2>
          <p>{t('draft.notFoundBody')}</p>
        </div>
      </div>
    );
  }

  const charCount = (draft.captionPl || '').length;
  const charLimit = CHAR_LIMITS[previewPlatform];
  const foldPoint = FOLD_POINTS[previewPlatform];
  const overFold = charCount > foldPoint;
  const platformLabel = t(`platforms.${previewPlatform}`);

  const allText = [
    draft.captionPl || '',
    '',
    [...(draft.hashtags || []), ...(draft.tags || [])].join(' '),
  ].join('\n').trim();

  const backLink = draft.eventId ? `/events/${draft.eventId}` : '/';
  const backLabel = draft.eventId ? 'draft.backToEvent' : 'draft.backToDashboard';

  return (
    <div>
      <Link to={backLink} className="draft-editor__back">{t(backLabel)}</Link>

      {usingMockData && <div className="dev-notice">{t('draft.loadError')}</div>}

      <header className="draft-editor__header">
        <div className="draft-editor__title-group">
          <h1>{t('draft.editor')}</h1>
          <span className="draft-editor__version">
            {t('draft.version', {
              version: draft.version,
              status: t(`draft.statuses.${draft.status}`),
            })}
          </span>
          {history.length > 0 && (
            <button
              type="button"
              className="button button--ghost"
              onClick={() => setShowHistory((v) => !v)}
              style={{ fontSize: 'var(--text-xs)', padding: '4px 10px' }}
            >
              {showHistory ? '▾' : '▸'} {history.length} versions
            </button>
          )}
        </div>
        <button
          type="button"
          className={'button ' + (showTranslation ? 'button--primary' : 'button--ghost')}
          onClick={() => setShowTranslation((v) => !v)}
          aria-pressed={showTranslation}
        >
          🇬🇧 {t('draft.translationToggle')}
        </button>
      </header>

      {showHistory && history.length > 0 && (
        <div className="caption-editor" style={{ marginBottom: 'var(--space-4)' }}>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {history.slice().reverse().map((entry) => (
              <li key={entry.id} className="page-header__subtitle">
                v{entry.version} · {entry.source} · {new Date(entry.createdAt).toLocaleString()}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="draft-editor__split">
        <div className="draft-editor__left">
          {/* Tone */}
          <section>
            <h3 className="section-title">{t('draft.tonesTitle')}</h3>
            <div className="filter-pills">
              {TONES.map((tone) => (
                <button
                  key={tone}
                  type="button"
                  className={'filter-pill' + (draft.tone === tone ? ' filter-pill--active' : '')}
                  onClick={() => {
                    updateLocal('tone', tone);
                    patchField('tone', tone);
                  }}
                >
                  {t(`draft.tones.${tone}`)}
                </button>
              ))}
            </div>
          </section>

          {/* Caption */}
          <div className="caption-editor">
            <textarea
              className="caption-editor__textarea"
              value={draft.captionPl || ''}
              onChange={(e) => {
                updateLocal('captionPl', e.target.value);
                scheduleCaptionAutosave();
              }}
              onBlur={handleCaptionBlur}
              aria-label={t('draft.caption')}
            />
            <div className="caption-editor__footer">
              <span className="caption-editor__count">
                {t('draft.charCount', { count: charCount, max: charLimit })}
              </span>
              <span className={overFold ? 'caption-editor__fold--over' : 'caption-editor__fold--ok'}>
                {overFold
                  ? t('draft.overFold', { platform: platformLabel, over: charCount - foldPoint })
                  : t('draft.underFold', { platform: platformLabel, limit: foldPoint })}
              </span>
            </div>
          </div>

          {/* Translation panel */}
          {showTranslation && draft.captionEn && (
            <div className="translation-panel">
              <div className="translation-panel__header">
                <span aria-hidden="true">🇬🇧</span> {t('draft.backTranslation')}
              </div>
              <div className="translation-panel__body">{draft.captionEn}</div>
            </div>
          )}

          {/* Hashtags */}
          <div className="hashtag-tiers">
            <h3 className="hashtag-tiers__title">{t('draft.hashtags')}</h3>
            <ChipRow
              chips={draft.hashtags || []}
              variant="terra"
              placeholder={t('draft.hashtagPlaceholder')}
              onAdd={addHashtag}
              onRemove={removeHashtag}
            />
          </div>

          {/* Tags */}
          <section className="chip-section">
            <header className="chip-section__header">
              <h3 className="chip-section__title">{t('draft.tags')}</h3>
            </header>
            <ChipRow
              chips={draft.tags || []}
              variant="navy"
              placeholder={t('draft.tagPlaceholder')}
              onAdd={addTag}
              onRemove={removeTag}
            />
          </section>
        </div>

        {/* RIGHT: platform preview */}
        <div className="draft-editor__right">
          <div className="preview-tabs" role="tablist">
            {PREVIEW_PLATFORMS.map((p) => (
              <button
                key={p}
                type="button"
                role="tab"
                aria-selected={previewPlatform === p}
                className={'tab' + (previewPlatform === p ? ' tab--active' : '')}
                onClick={() => setPreviewPlatform(p)}
              >
                {t(`platforms.${p}`)}
              </button>
            ))}
          </div>

          <PlatformMockup platform={previewPlatform} caption={draft.captionPl || ''} foldPoint={foldPoint} />

          <div className="preview-fold-note">
            {t('draft.preview.foldAt', { count: foldPoint })}
          </div>
        </div>
      </div>

      {/* Bottom action bar */}
      <div className="draft-editor__action-bar">
        <div className="draft-editor__action-group">
          <button
            type="button"
            className="button button--reject"
            onClick={() => handleStatusChange('rejected')}
            disabled={draft.status === 'rejected'}
          >
            {t('draft.reject')}
          </button>
          <button
            type="button"
            className="button button--changes"
            onClick={() => handleStatusChange('pending_approval')}
            disabled={draft.status === 'pending_approval'}
          >
            {t('draft.requestChanges')}
          </button>
          <button
            type="button"
            className="button button--ghost button--ghost--danger"
            onClick={() => setShowDelete(true)}
          >
            {t('common.delete')}
          </button>
        </div>
        <div className="draft-editor__action-group">
          <button type="button" className="button button--ghost" disabled>
            {t('draft.regenerate')}
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => copyToClipboard(draft.captionPl || '')}
          >
            {copyFlash ? '✓ ' + t('draft.copySuccess') : t('draft.copyCaption')}
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={() => copyToClipboard(allText)}
          >
            {t('draft.copyAll')}
          </button>
          <button type="button" className="button button--ghost" disabled>
            {t('draft.sharePreview')}
          </button>
          <button
            type="button"
            className="button button--approve"
            onClick={() => handleStatusChange('approved')}
            disabled={draft.status === 'approved'}
          >
            ✓ {t('draft.approve')}
          </button>
        </div>
      </div>

      {showDelete && (
        <ConfirmDeleteDialog
          onCancel={() => setShowDelete(false)}
          onConfirm={handleDelete}
          deleting={deleting}
        />
      )}

      <SaveToast status={saveStatus} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ChipRow — flat list of chips with × remove + add input.
// ─────────────────────────────────────────────────────────────────────────────
function ChipRow({ chips, variant, placeholder, onAdd, onRemove }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');

  function commit() {
    const value = draft.trim();
    if (!value) return;
    onAdd(value);
    setDraft('');
  }

  return (
    <div>
      {chips.length > 0 && (
        <div className="chip-section__chips" style={{ marginBottom: 'var(--space-2)' }}>
          {chips.map((chip) => (
            <span key={chip} className={'chip' + (variant ? ` chip--${variant}` : '')}>
              <span>{chip}</span>
              <button
                type="button"
                className="chip__remove"
                onClick={() => onRemove(chip)}
                aria-label={`${t('common.remove')} ${chip}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="chip-input">
        <input
          type="text"
          className="chip-input__field"
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
        />
        <button type="button" className="chip-input__add" onClick={commit} aria-label={t('common.add')}>
          +
        </button>
      </div>
    </div>
  );
}
